const BUILD_VERSION_META = 'meta[name="build-version"]';
const BUILD_VERSION_PATTERN =
  /<meta\s+name="build-version"\s+content="([^"]*)"/i;

/** Integer deploy version embedded at build time (see deploy-version.json). */
export function getBundledBuildVersion(): string {
  return import.meta.env.VITE_BUILD_VERSION?.trim() || "dev";
}

export function parseBuildVersionFromHtml(html: string): string | null {
  const match = html.match(BUILD_VERSION_PATTERN);
  return match?.[1]?.trim() || null;
}

export function readBuildVersionFromDocument(doc: Document = document): string | null {
  return doc.querySelector(BUILD_VERSION_META)?.getAttribute("content")?.trim() || null;
}

/**
 * Fetch the live shell HTML and read its deploy version meta tag.
 * Uses cache-busting so intermediaries return the latest deploy.
 */
export async function fetchLatestBuildVersion(
  indexUrl: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  const separator = indexUrl.includes("?") ? "&" : "?";
  const response = await fetchImpl(`${indexUrl}${separator}_v=${Date.now()}`, {
    cache: "no-store",
  });

  if (!response.ok) {
    return null;
  }

  return parseBuildVersionFromHtml(await response.text());
}

export function shouldReloadForBuildVersion(
  bundledVersion: string,
  latestVersion: string | null,
): boolean {
  if (!latestVersion || latestVersion === "dev") {
    return false;
  }

  if (!bundledVersion || bundledVersion === "dev") {
    return false;
  }

  return latestVersion !== bundledVersion;
}

export type DeployVersionCheckOptions = {
  bundledVersion?: string;
  indexUrl?: string;
  pollIntervalMs?: number;
  enabled?: boolean;
  fetchImpl?: typeof fetch;
  reload?: () => void;
  onStaleVersion?: (latestVersion: string) => void;
};

/**
 * Poll the deployed shell for a newer build version and reload when stale.
 * No-op in dev and when the bundled version is missing.
 */
export function startDeployVersionCheck(
  options: DeployVersionCheckOptions = {},
): () => void {
  const bundledVersion = options.bundledVersion ?? getBundledBuildVersion();
  if (bundledVersion === "dev") {
    return () => {};
  }

  const indexUrl =
    options.indexUrl ??
    `${import.meta.env.BASE_URL.replace(/\/?$/, "/")}index.html`;
  const pollIntervalMs = options.pollIntervalMs ?? 60_000;
  const fetchImpl = options.fetchImpl ?? fetch;
  const reload = options.reload ?? (() => window.location.reload());
  const enabled =
    options.enabled ?? (!import.meta.env.DEV && bundledVersion !== "dev");

  if (!enabled) {
    return () => {};
  }

  let checking = false;

  const check = async () => {
    if (checking || document.visibilityState === "hidden") {
      return;
    }

    checking = true;
    try {
      const latestVersion = await fetchLatestBuildVersion(indexUrl, fetchImpl);
      if (shouldReloadForBuildVersion(bundledVersion, latestVersion)) {
        options.onStaleVersion?.(latestVersion!);
        reload();
      }
    } catch {
      // Ignore transient network errors; next poll or focus will retry.
    } finally {
      checking = false;
    }
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void check();
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  const intervalId = window.setInterval(() => {
    void check();
  }, pollIntervalMs);

  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.clearInterval(intervalId);
  };
}
